import React from "react";
import dynamic from "next/dynamic";
import Head from "next/head";
import { useParams, usePathname } from "next/navigation";

import { CATEGORY_NAMES } from "@/constants/dispensary";
import { Action, Category } from "@/constants/events";
import useDomain from "@/hooks/useDomain";
import { useEventTracker } from "@/hooks/useEventTracker";
import useFilters from "@/hooks/useFilters";
import useShop, { isCategoryHub } from "@/hooks/useShop";
import useShopSearchParams from "@/hooks/useShopSearchParams";
import { createGetServerSideProps } from "@/utils/createGetServerSideProps";

import FilterBar from "@/components/FilterBar";
import ShopMenuItemCarousels from "@/components/search-shop/ShopMenuItemCarousels";
import ShopResultsDescription from "@/components/search-shop/ShopResultsDescription";
import ShopSort from "@/components/search-shop/ShopSort";
import SearchAd from "@/components/SearchAd";
import DealsCarousel from "@/components/Shared/DealsCarousel";
import RecentlyViewedMenuItems from "@/components/Shared/RecentlyViewedMenuItems";
import PromotedStrainCarousel from "@/components/StrainCarousel";

const ShopSelection = dynamic(
  () => import("@/components/search-shop/ShopSelection"),
);
const PhenotypeCarousel = dynamic(
  () => import("@/components/PhenotypeCarousel"),
);

const AllResultsButton = dynamic(() => import("@/components/AllResultsButton"));

const ShopPage: React.FC = () => {
  const asPath = usePathname();
  const baseUrl = useDomain();
  const params = useParams();
  const slug = params?.slug;
  const { publishEvent } = useEventTracker();
  const shopSearchParams = useShopSearchParams();
  const {
    shopCategory,
    shopName,
    product_category,
    strain_category,
    strain_name,
    q,
  } = shopSearchParams;

  const {
    showEmptyState,
    dispensaries,
    totalCount,
    filters,
    selection,
    loading,
  } = useShop();
  const { addFilter } = useFilters();

  const categoryHub = isCategoryHub(shopSearchParams);
  const isPromotedStrains =
    slug && slug.length === 1 && slug[0] === "promoted-strains";
  const isPhenotype = slug && slug.length === 1 && slug[0] === "phenotype";

  const adTargeting: { [key: string]: string } = {};
  let title = "Shop Leafly";
  if (categoryHub) {
    adTargeting.category = String(product_category);
    title = `Order ${CATEGORY_NAMES[String(product_category)]} online`;
  } else if (isPhenotype && strain_category) {
    adTargeting.phenotype = String(strain_category);
    title = `Order ${strain_category} online`;
  } else if (shopCategory?.includes("brand") && shopName) {
    adTargeting.brand = String(shopName);
    title = `Order ${shopName} products online`;
  } else if (shopCategory?.includes("strain") && shopName) {
    adTargeting.strain = String(shopName);
    title = `Order ${shopName} ${product_category || ""} online`;
  } else if (isPromotedStrains && strain_name) {
    adTargeting.promotedStrain = String(strain_name);
    title = `Order ${strain_name} on sale!`;
  } else if (q) {
    title = `Search results for "${q}"`;
  }

  return (
    <>
      <Head>
        <meta property="og:url" content={`${baseUrl}${asPath}`} key="og:url" />
        <title>Shop | Leafly</title>
        <meta name="robots" content="noindex,noarchive,nofollow" />
      </Head>

      <section className="container pt-lg">
        <h1 className="heading--m mb-sm md:mb-none">{title}</h1>

        {slug && slug.length === 0 && (
          <ShopSelection
            filters={filters}
            selection={selection}
            className="mb-md empty:hidden"
            loading={loading}
          />
        )}
        {isPromotedStrains && (
          <PromotedStrainCarousel
            onClick={(strain) => {
              addFilter({
                strain_name: strain.strainName,
              });

              publishEvent({
                action: Action.click,
                category: Category.dealsOnTopStrains,
                label: `strain hub quick access - ${strain.strainName}`,
              });
            }}
          />
        )}
        {isPhenotype && <PhenotypeCarousel className="mt-lg mb-md" />}

        <FilterBar
          filters={filters}
          selectionId={selection?.id}
          showEmptyState={showEmptyState}
          loading={loading}
        />

        <div className="mb-sm md:flex justify-between">
          <ShopResultsDescription
            totalCount={totalCount}
            showEmptyState={showEmptyState}
            loading={loading}
          />
          <ShopSort showEmptyState={showEmptyState} loading={loading} />
        </div>
        {showEmptyState && <AllResultsButton className="my-lg" />}
      </section>

      <section className="bg-leafly-white pt-xl pb-xxl">
        <SearchAd
          tag="availability"
          customTargeting={adTargeting}
          className="mb-xl"
        />
        <ShopMenuItemCarousels dispensaries={dispensaries} loading={loading} />
      </section>

      <DealsCarousel className="container my-xxl" />

      <RecentlyViewedMenuItems
        className="container mt-xxl mb-section"
        onCardImpression={(menuItem) => {
          publishEvent({
            action: Action.impression,
            category: Category.recentlyViewedMenuItems,
            dispensaryId: menuItem.dispensaryId,
          });
        }}
      />
    </>
  );
};

export const getServerSideProps = createGetServerSideProps(async (context) => {
  const slugs = context.query.slug || [];
  const isPromotedStrains =
    slugs.length === 1 && slugs[0] === "promoted-strains";
  const isPhenotype = slugs.length === 1 && slugs[0] === "phenotype";

  // This file covers only /search/shop, /search/shop/phenotype, and /search/shop/promoted-strains
  if (slugs.length === 0 || isPromotedStrains || isPhenotype) {
    return {
      props: {},
    };
  } else {
    return {
      notFound: true,
    };
  }
});

export default ShopPage;
